import React, { useEffect } from 'react';
import Lottie from 'lottie-react';
import { motion, Variants } from 'framer-motion';
import confetti from 'canvas-confetti';
import successAnimation from '../../assets/success-animation.json';

interface SigningSuccessProps {
  patientName: string;
  formsSubmitted: boolean;
  onReset: () => void;
  resetLabel?: string;
}

export default function SigningSuccess({ patientName, formsSubmitted, onReset, resetLabel = 'Done' }: SigningSuccessProps) {
  useEffect(() => {
    // Fire confetti on mount
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="text-center pt-0 pb-6 px-4 flex flex-col items-center relative"
    >
      <motion.div variants={itemVariants} className="w-28 h-28 sm:w-40 sm:h-40 mx-auto -mt-20 -mb-6 relative">
        <div className="absolute inset-0 bg-primary/10 rounded-full blur-3xl" />
        <Lottie
          animationData={successAnimation}
          loop={false}
          autoplay={true}
          style={{ width: '100%', height: '100%', position: 'relative', zIndex: 1 }}
        />
      </motion.div>

      <div className="space-y-2 relative z-10">
        <motion.h2
          variants={itemVariants}
          className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight"
        >
          {formsSubmitted ? 'All Set!' : 'Check-in complete'}
        </motion.h2>
        
        <motion.p 
          variants={itemVariants}
          className="text-base sm:text-lg font-medium text-gray-500 max-w-[240px] mx-auto leading-relaxed"
        >
          {formsSubmitted ? (
            <>Your forms have been <span className="text-primary-deep font-bold">submitted successfully</span>.</>
          ) : "You're checked in. Please have a seat and the front desk will be with you shortly."}
        </motion.p>

        <motion.p
          variants={itemVariants}
          className="text-sm text-gray-500 pt-1"
        >
          Thank you, <span className="text-gray-600 font-semibold">{patientName}</span>.
        </motion.p>
      </div>

      <motion.div variants={itemVariants} className="pt-8 w-full max-w-[200px] relative z-10">
        <button
          className="btn-primary w-full shadow-lg shadow-primary-deep/20"
          onClick={onReset}
        >
          {resetLabel}
        </button>
      </motion.div>
    </motion.div>
  );
}
